import { APIClient, ApiQueryParams } from '@frontend/api-utils';
import { EntityType } from '@frontend/common';
import {
    CreateGroup,
    CreateGroupMember,
    GroupListResponse,
    GroupMember,
    GroupMemberListResponse,
    GroupMemberQueryParams,
    GroupQueryParams,
    IGroup,
    UpdateGroup,
    UpdateGroupMember
} from '@frontend/group/types';

export class ProductGroupClient extends APIClient {
    public static readonly endpoint = '/group-api/v1';

    public static async fetchProductGroups(queryParams?: ApiQueryParams<GroupQueryParams>): Promise<GroupListResponse> {
        return await this.apiPaginated<GroupListResponse, GroupQueryParams>(`${this.endpoint}/product-groups`, queryParams, undefined);
    }

    public static async resolveProductGroups(ids: string[], field: string = 'id'): Promise<GroupListResponse> {
        return this.resolve<GroupListResponse>(`${this.endpoint}/product-groups`, ids, field);
    }

    public static async deleteProductGroups(groupIds: string[]): Promise<void> {
        const response = await this.post(`${this.endpoint}/product-groups-delete`, { ids: groupIds });
        return await this.handleVoidResponse(response);
    }

    public static async postProductGroup(accountId: string, group: CreateGroup): Promise<IGroup> {
        const response = await this.post(`${this.endpoint}/accounts/${accountId}/product-groups`, group);
        return await this.handleResponse<IGroup>(response);
    }

    public static async fetchProductGroup(accountId: string, groupId: string): Promise<IGroup> {
        const response = await this.fetch(`${this.endpoint}/accounts/${accountId}/product-groups/${groupId}`);
        return await this.handleResponse<IGroup>(response);
    }

    public static async patchProductGroup(accountId: string, groupId: string, group: UpdateGroup): Promise<IGroup> {
        const response = await this.patch(`${this.endpoint}/accounts/${accountId}/product-groups/${groupId}`, group);
        return await this.handleResponse<IGroup>(response);
    }

    public static async deleteProductGroup(accountId: string, groupId: string): Promise<void> {
        const response = await this.delete(`${this.endpoint}/accounts/${accountId}/product-groups/${groupId}`);
        return await this.handleVoidResponse(response);
    }

    public static async fetchGroupsNotInGroup(accountId: string, groupId: string): Promise<GroupListResponse> {
        const response = await this.fetch(`${this.endpoint}/accounts/${accountId}/product-groups/${groupId}/groups`);
        return await this.handleResponse<GroupListResponse>(response);
    }

    //IGroup members
    public static async fetchProductGroupMembers(queryParams?: ApiQueryParams<GroupMemberQueryParams>): Promise<GroupMemberListResponse> {
        return await this.apiPaginated<GroupMemberListResponse, GroupMemberQueryParams>(`${this.endpoint}/product-group-members`, queryParams);
    }

    public static async resolveProductGroupMembers(ids: string[], field: string = 'id'): Promise<GroupMemberListResponse> {
        return this.resolve<GroupMemberListResponse>(`${this.endpoint}/product-group-members`, ids, field);
    }

    public static async deleteProductGroupMembers(memberIds: string[]): Promise<void> {
        const response = await this.post(`${this.endpoint}/product-group-members-delete`, { ids: memberIds });
        return await this.handleVoidResponse(response);
    }

    public static async postProductGroupMember(accountId: string, groupId: string, groupMember: CreateGroupMember): Promise<GroupMember> {
        const response = await this.post(`${this.endpoint}/accounts/${accountId}/product-groups/${groupId}/members`, groupMember);
        return await this.handleResponse<GroupMember>(response);
    }

    public static async postProductGroupMemberList(accountId: string, groupId: string, entity_type: EntityType, memberIds: string[]): Promise<void> {
        const response = await this.post(`${this.endpoint}/accounts/${accountId}/product-groups/${groupId}/${entity_type}s/members-list`, { ids: memberIds });
        return await this.handleVoidResponse(response);
    }

    public static async fetchProductGroupMember(accountId: string, groupId: string, memberId: string): Promise<GroupMember> {
        const response = await this.fetch(`${this.endpoint}/accounts/${accountId}/product-groups/${groupId}/members/${memberId}`);
        return await this.handleResponse<GroupMember>(response);
    }

    public static async patchProductGroupMember(accountId: string, groupId: string, memberId: string, body: UpdateGroupMember): Promise<GroupMember> {
        const response = await this.patch(`${this.endpoint}/accounts/${accountId}/product-groups/${groupId}/members/${memberId}`, body);
        return await this.handleResponse<GroupMember>(response);
    }

    public static async deleteProductGroupMember(accountId: string, groupId: string, memberId: string): Promise<void> {
        const response = await this.delete(`${this.endpoint}/accounts/${accountId}/product-groups/${groupId}/members/${memberId}`);
        return await this.handleVoidResponse(response);
    }
}
